"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.stockCardResponse = exports.stockBalance = exports.stockCardMovement = exports.stockCardQuery = exports.stockValuationResponse = exports.stockValuationAccount = exports.stockOnHandResponse = exports.stockOnHandRow = exports.stockAsOfQuery = exports.stockDocumentDto = exports.stockDocumentLineDto = exports.createStockAdjustmentRequest = exports.createStockOpeningRequest = exports.stockMovementKind = void 0;
const zod_1 = require("zod");
const common_1 = require("./common");
/**
 * المخزون — ميزة اختيارية يحكمها `settings.inventory.enabled` (ADR-0006). الحركات
 * تنشأ من مستنداتها (فاتورة مورد، فاتورة بيع، إشعار دائن، إلغاء فاتورة) أو من مستندى
 * المخزون هنا: الرصيد الافتتاحى والجرد/التسوية. الحركة لا تُعدَّل ولا تُحذف.
 */
/** كمية موجبة بأربعة أرقام عشرية على الأكثر — نص كالمال لا number. */
const positiveQuantity = zod_1.z
    .string()
    .regex(/^\d{1,12}(\.\d{1,4})?$/, 'quantity must be a decimal string with up to 4 decimals')
    .refine((value) => /[1-9]/.test(value), 'quantity must be positive');
/** كمية معدودة: صفر مسموح (الصنف نفد فعلاً)، والسالب لا. */
const countedQuantity = zod_1.z
    .string()
    .regex(/^\d{1,12}(\.\d{1,4})?$/, 'quantity must be a decimal string with up to 4 decimals');
const unitCost = common_1.moneyString.refine((value) => !value.startsWith('-'), 'cost cannot be negative');
exports.stockMovementKind = zod_1.z.enum([
    'opening',
    'purchase',
    'sale',
    'sale_return',
    'sale_cancellation',
    'adjustment',
]);
/**
 * رصيد افتتاحى لأصناف مخزنية: مدين حساب مخزون كل صنف، ودائن حساب الافتتاح (حساب فروق
 * الأرصدة الافتتاحية، أو الأرباح المرحلة حسب `opening.difference`) — BLOCKED-ACCOUNTING.
 */
exports.createStockOpeningRequest = zod_1.z
    .object({
    documentDate: common_1.isoDate,
    memo: zod_1.z.string().trim().max(500).optional(),
    lines: zod_1.z
        .array(zod_1.z.object({ itemId: common_1.uuid, quantity: positiveQuantity, unitCost }).strict())
        .min(1)
        .max(500),
})
    .strict();
/**
 * جرد وتسوية: الكمية المعدودة لكل صنف، والفرق عن الدفترى يُرحَّل على حساب فروق الجرد
 * (BLOCKED-ACCOUNTING). `unitCost` لتسعير الزيادة حين لا تكلفة معروفة للصنف.
 */
exports.createStockAdjustmentRequest = zod_1.z
    .object({
    documentDate: common_1.isoDate,
    reason: zod_1.z.string().trim().min(3).max(500),
    lines: zod_1.z
        .array(zod_1.z
        .object({
        itemId: common_1.uuid,
        countedQuantity,
        unitCost: unitCost.optional(),
    })
        .strict())
        .min(1)
        .max(500),
})
    .strict();
exports.stockDocumentLineDto = zod_1.z.object({
    itemId: common_1.uuid,
    itemName: zod_1.z.string(),
    /** بإشارة الحركة: موجبة للوارد وسالبة للعجز. */
    quantity: zod_1.z.string(),
    value: common_1.moneyString,
    /** للجرد فقط: الكمية المعدودة. */
    countedQuantity: zod_1.z.string().nullable(),
});
exports.stockDocumentDto = zod_1.z.object({
    id: common_1.uuid,
    companyId: common_1.uuid,
    kind: zod_1.z.enum(['opening', 'adjustment']),
    number: zod_1.z.string(),
    documentDate: common_1.isoDate,
    reason: zod_1.z.string().nullable(),
    /** صافى أثر المستند على المخزون. */
    totalValue: common_1.moneyString,
    journalId: common_1.uuid,
    journalNumber: zod_1.z.string().nullable(),
    lines: zod_1.z.array(exports.stockDocumentLineDto),
    createdAt: zod_1.z.string(),
});
exports.stockAsOfQuery = zod_1.z
    .object({
    /** إن غاب: اليوم. */
    asOf: common_1.isoDate.optional(),
})
    .strict();
exports.stockOnHandRow = zod_1.z.object({
    itemId: common_1.uuid,
    nameAr: zod_1.z.string(),
    unit: zod_1.z.string(),
    isActive: zod_1.z.boolean(),
    inventoryAccountCode: common_1.accountCode,
    quantity: zod_1.z.string(),
    /** القيمة ÷ الكمية بأربعة أرقام، وصفر حين لا رصيد موجب. */
    averageCost: common_1.moneyString,
    value: common_1.moneyString,
});
exports.stockOnHandResponse = zod_1.z.object({
    asOf: common_1.isoDate,
    items: zod_1.z.array(exports.stockOnHandRow),
    totalValue: common_1.moneyString,
});
/** تقييم المخزون مقابل رصيد حساباته فى الأستاذ — الفرق صفر حين يكون كل شىء من مستنداته. */
exports.stockValuationAccount = zod_1.z.object({
    accountCode: common_1.accountCode,
    nameAr: zod_1.z.string(),
    stockValue: common_1.moneyString,
    ledgerBalance: common_1.moneyString,
    difference: common_1.moneyString,
});
exports.stockValuationResponse = zod_1.z.object({
    asOf: common_1.isoDate,
    accounts: zod_1.z.array(exports.stockValuationAccount),
    totalStockValue: common_1.moneyString,
    totalLedgerBalance: common_1.moneyString,
    difference: common_1.moneyString,
    balanced: zod_1.z.boolean(),
});
exports.stockCardQuery = zod_1.z
    .object({
    from: common_1.isoDate.optional(),
    to: common_1.isoDate.optional(),
})
    .strict();
exports.stockCardMovement = zod_1.z.object({
    id: common_1.uuid,
    date: common_1.isoDate,
    kind: exports.stockMovementKind,
    /** رقم المستند المصدر (فاتورة، إشعار، مستند مخزون). */
    reference: zod_1.z.string(),
    sourceType: zod_1.z.string(),
    sourceId: common_1.uuid,
    quantityIn: zod_1.z.string(),
    quantityOut: zod_1.z.string(),
    /** قيمة الحركة بإشارتها. */
    value: common_1.moneyString,
    balanceQuantity: zod_1.z.string(),
    balanceValue: common_1.moneyString,
    averageCost: common_1.moneyString,
    journalId: common_1.uuid,
});
exports.stockBalance = zod_1.z.object({ quantity: zod_1.z.string(), value: common_1.moneyString });
exports.stockCardResponse = zod_1.z.object({
    item: zod_1.z.object({
        id: common_1.uuid,
        nameAr: zod_1.z.string(),
        unit: zod_1.z.string(),
        inventoryAccountCode: common_1.accountCode,
    }),
    from: common_1.isoDate.nullable(),
    to: common_1.isoDate.nullable(),
    opening: exports.stockBalance,
    movements: zod_1.z.array(exports.stockCardMovement),
    closing: exports.stockBalance,
});
//# sourceMappingURL=inventory.js.map